import { AnyLayer, Map } from 'mapbox-gl';

import { MonthToNumberLookup } from '../constants/mapLayers/catchPositions';
import {
  MAPBOX_LAYERS,
  ORDERED_DEFAULT_MAPBOX_LAYERS,
  ORDERED_FISHBRAIN_LAYERS,
} from '../mapData/layers';
import {
  CATCH_POSITIONS_HIGHLIGHT_LAYER,
  CATCH_POSITIONS_SIMPLIFIED_HIGHLIGHT_LAYER,
} from '../mapData/mapLayers/catchPositions';
import { FISHING_WATERS_HIGHLIGHT_LAYER } from '../mapData/mapLayers/fishingWaters';
import { CatchFilters, FishbrainLayer, LayerName, Layers, ToggleableGroup } from '../types/types';

type FilterCatchPositions = (map: Map, catchFilters: CatchFilters) => void;
type FilterWater = (map: Map, waterId: string) => void;
type FilterLayerBasedOnToggleableGroups = (params: {
  map: Map;
  layerName: LayerName;
  toggleableGroups: ToggleableGroup[];
}) => void;
type FilterLayersBasedOnToggleableGroups = (params: { map: Map; layers: Layers }) => void;
type EditMapboxLayers = (map: Map, fibLayers: FishbrainLayer[]) => void;

const filterWater: FilterWater = (map, waterId) => {
  MAPBOX_LAYERS.fishingWaters.mapboxLayers
    .filter(mapboxLayer => mapboxLayer.id !== FISHING_WATERS_HIGHLIGHT_LAYER.id)
    .forEach(mapboxLayer => {
      if (map.getLayer(mapboxLayer.id)) {
        map.setFilter(mapboxLayer.id, ['==', 'external_id', waterId]);
      }
    });
};

const filterCatchPositions: FilterCatchPositions = (map, catchFilters) => {
  const { months, speciesIds } = catchFilters;
  const hasMonthFilter = months && months.length > 0;
  const hasSpeciesFilter = speciesIds && speciesIds.length > 0;

  MAPBOX_LAYERS.catchPositions.mapboxLayers
    .filter(mapboxLayer => mapboxLayer.id !== CATCH_POSITIONS_HIGHLIGHT_LAYER.id)
    .filter(mapboxLayer => mapboxLayer.id !== CATCH_POSITIONS_SIMPLIFIED_HIGHLIGHT_LAYER.id)
    .forEach(mapboxLayer => {
      if (map.getLayer(mapboxLayer.id)) {
        const filters = [];
        const monthsAsNumbers: number[] = [];

        if (mapboxLayer.filter) {
          filters.push(mapboxLayer.filter);
        }

        if (hasMonthFilter && hasSpeciesFilter) {
          months.forEach(month => monthsAsNumbers.push(MonthToNumberLookup[month]));
          filters.push(['in', 'species_id', ...speciesIds]);
          filters.push(['in', 'caught_at_month', ...monthsAsNumbers]);
        } else if (hasMonthFilter) {
          months.forEach(month => monthsAsNumbers.push(MonthToNumberLookup[month]));
          filters.push(['in', 'caught_at_month', ...monthsAsNumbers]);
        } else if (hasSpeciesFilter) {
          filters.push(['in', 'species_id', ...speciesIds]);
        }

        if (filters.length > 0) {
          map.setFilter(mapboxLayer.id, ['all', ...filters]);
        } else {
          map.setFilter(mapboxLayer.id, null);
        }
      }
    });
};

const filterLayerBasedOnToggleableGroups: FilterLayerBasedOnToggleableGroups = ({
  map,
  layerName,
  toggleableGroups,
}) => {
  MAPBOX_LAYERS[layerName].mapboxLayers.forEach(mapboxLayer => {
    if (!map.getLayer(mapboxLayer.id) || mapboxLayer.toggleableGroups === null) {
      return;
    }

    const isVisible = mapboxLayer.toggleableGroups.some(toggleableGroup =>
      toggleableGroups.includes(toggleableGroup),
    );

    map.setLayoutProperty(mapboxLayer.id, 'visibility', isVisible ? 'visible' : 'none');
  });
};

const filterLayersBasedOnToggleableGroups: FilterLayersBasedOnToggleableGroups = ({
  map,
  layers,
}) => {
  Object.entries(layers).forEach(([layerName, layer]) => {
    filterLayerBasedOnToggleableGroups({
      map,
      layerName: layerName as LayerName,
      toggleableGroups: layer.toggleableGroups,
    });
  });
};

const addMapboxLayers: EditMapboxLayers = (map, fibLayers) => {
  ORDERED_FISHBRAIN_LAYERS.forEach(layer => {
    if (fibLayers.includes(layer.fishbrainLayer)) {
      layer.mapboxLayer.mapboxLayers.forEach(mapboxLayer => {
        if (map.getSource(layer.mapboxLayer.sourceName) && !map.getLayer(mapboxLayer.id)) {
          map.addLayer(mapboxLayer as AnyLayer);
        }
      });
    }
  });
};

const removeMapboxLayers: EditMapboxLayers = (map, fibLayers) => {
  ORDERED_FISHBRAIN_LAYERS.forEach(layer => {
    if (!fibLayers.includes(layer.fishbrainLayer)) {
      layer.mapboxLayer.mapboxLayers.forEach(mapboxLayer => {
        if (map.getLayer(mapboxLayer.id)) {
          map.removeLayer(mapboxLayer.id);
        }
      });
    }
  });
};

const orderMapboxLayers: EditMapboxLayers = (map, fibLayers) => {
  ORDERED_FISHBRAIN_LAYERS.forEach(layer => {
    if (fibLayers.includes(layer.fishbrainLayer)) {
      layer.mapboxLayer.mapboxLayers.forEach(mapboxLayer => {
        if (map.getLayer(mapboxLayer.id)) {
          map.moveLayer(mapboxLayer.id);
        }
      });
    }
  });
  ORDERED_DEFAULT_MAPBOX_LAYERS.forEach(defaultMapboxLayer => {
    if (map.getLayer(defaultMapboxLayer)) {
      map.moveLayer(defaultMapboxLayer);
    }
  });
};

export {
  filterWater,
  filterCatchPositions,
  addMapboxLayers,
  removeMapboxLayers,
  orderMapboxLayers,
  filterLayersBasedOnToggleableGroups,
};
